'use client'

/**
 * Things arriving one at a time, ported from GolfTec
 * `components/shell/Reveal.tsx` with no domain change.
 *
 * `Reveal` staggers a list by index. `Typewriter` types a line out once per
 * distinct text, so a poll returning the same sentence does not retype it.
 * Both respect prefers-reduced-motion: with motion reduced they simply appear.
 */

import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

/** Shared easing for arrivals (plan 08 section 3.2). */
export const REVEAL_EASE = [0.25, 0.46, 0.45, 0.94] as const

/** One item in a sequence, delayed by its position. */
export function Reveal({
  index = 0,
  step = 0.22,
  children,
  className,
}: {
  index?: number
  /** Seconds between neighbours. */
  step?: number
  children: React.ReactNode
  className?: string
}) {
  const reduced = useReducedMotion()
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 8, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: reduced ? 0 : index * step, duration: 0.34, ease: REVEAL_EASE }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

/**
 * A line typed out once per distinct text. The visible characters are
 * aria-hidden; the caller puts the full sentence in an aria-label or a
 * visually hidden live copy so screen readers hear it once (Rule 14).
 */
export function Typewriter({
  text,
  className,
  speed = 18,
  startDelay = 250,
}: {
  text: string
  className?: string
  /** Milliseconds per character. */
  speed?: number
  startDelay?: number
}) {
  const reduced = useReducedMotion()
  const [shown, setShown] = useState(reduced ? text : '')
  const typedRef = useRef<string | null>(null)

  useEffect(() => {
    if (reduced) {
      setShown(text)
      return
    }
    if (typedRef.current === text) return
    typedRef.current = text

    setShown('')
    let index = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const begin = setTimeout(() => {
      const tick = () => {
        index += 1
        setShown(text.slice(0, index))
        if (index < text.length) timer = setTimeout(tick, speed)
      }
      tick()
    }, startDelay)

    return () => {
      clearTimeout(begin)
      if (timer) clearTimeout(timer)
      // A cleanup means this typing run was cut short (a new sentence, or React's development remount): type again next run.
      typedRef.current = null
    }
  }, [text, speed, startDelay, reduced])

  return (
    <span className={className} aria-hidden='true'>
      {shown}
      {!reduced && shown.length < text.length && (
        <motion.span
          animate={{ opacity: [1, 0, 1] }}
          transition={{ repeat: Infinity, duration: 0.9 }}
          className='ml-0.5 inline-block h-[0.95em] w-[2px] translate-y-[0.12em] bg-brand-cornflower'
        />
      )}
    </span>
  )
}
